import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ENDPOINTS } from '@/config/endpoints';
import type {
    UserInfo,
    CategoryResource,
    EventData,
    Mail,
    LastSeen,
    AgendaResponse,
    AdminData,
    Category,
    Settings,
    ScheduleItem,
    ScheduleTemplate,
    RecentSchedule,
    MigrationResponse,
    Event,
    MonthlyRecurrencePattern,
    YearlyRecurrencePattern,
    InstallPostRequest,
    InstallPostResponse,
    CategoryRequest,
    ScheduleTemplateSet,
    EventAuditEntry,
    MetricsData,
    MetricsEventPayload,
    ScreenshotMeta,
} from '@/types';
import type { InstallationStatus } from '@/types/migration';
import { addChurchToolsServices } from '@/store/cacheSlice';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/';

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: API_BASE_URL,
        credentials: 'include', // Include cookies for authentication
        prepareHeaders: (headers) => {
            headers.set('Accept', '*/*');
            headers.set('Content-Type', 'application/json');
            return headers;
        },
    }),
    tagTypes: [
        'UserInfo',
        'Plan',
        'Categories',
        'Mail',
        'LastSeen',
        'ResourceSettings',
        'Admin',
        'Settings',
        'Schedule',
        'ScheduleTemplates',
        'ScheduleTemplateSets',
        'Migrations',
        'CategoryRequests',
        'PlanResourcesAudit',
        'Metrics',
        'Screenshots',
    ],
    endpoints: (builder) => ({
        getUserInfo: builder.query<UserInfo, void>({
            query: () => 'rest/UserInfo',
            providesTags: ['UserInfo'],
            transformResponse: (response: UserInfo) => {
                // Keep localStorage in sync so ThemeProvider reads the right default on next load
                const theme = response.theme ?? 'dark';
                try {
                    localStorage.setItem('theme', theme);
                } catch {
                    /* ignore */
                }
                return response;
            },
        }),
        updateUserInfo: builder.mutation<void, Partial<UserInfo>>({
            query: (data) => ({
                url: ENDPOINTS.userInfo(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['UserInfo'],
            onQueryStarted: async (data) => {
                // Optimistically write to localStorage so the toggle feels instant
                if (data.theme) {
                    try {
                        localStorage.setItem('theme', data.theme);
                    } catch {
                        /* ignore */
                    }
                }
            },
        }),
        getUsers: builder.query<{ name: string }[], void>({
            query: () => ENDPOINTS.userNames(),
            providesTags: ['UserInfo'],
        }),
        authenticate: builder.mutation<
            { success: boolean; code?: string; details?: string },
            { code: string }
        >({
            query: (data) => ({
                url: ENDPOINTS.authenticate(),
                method: 'POST',
                body: data,
            }),
        }),
        logout: builder.mutation<{ success: string; oidc: boolean; logoutUrl?: string }, void>({
            query: () => ({
                url: `${ENDPOINTS.authenticate()}/logout`,
                method: 'GET',
            }),
            invalidatesTags: ['UserInfo'],
        }),
        getSessionTimestamp: builder.query<
            {
                categoriesUpdated: string | null;
                indexUpdated?: string | null;
                unauthenticated?: boolean;
            },
            void
        >({
            query: () => '/rest/Session',
            keepUnusedDataFor: 0, // Never cache - always fetch fresh from server
        }),
        reloadSession: builder.mutation<
            {
                success: boolean;
                message: string;
                categoriesAssigned: number[];
                categoriesResponsible: number[];
                categoriesRepresents: number[];
                represents: number[];
                categoriesUpdated: string | null;
            },
            void
        >({
            query: () => ({
                url: '/rest/Session',
                method: 'POST',
            }),
            invalidatesTags: ['UserInfo', 'Categories'],
        }),

        getSettings: builder.query<Settings, void>({
            query: () => ENDPOINTS.settings(),
            providesTags: ['Settings'],
        }),

        getLastSeen: builder.query<LastSeen[], void>({
            query: () => ENDPOINTS.lastSeen(),
            providesTags: ['LastSeen'],
        }),

        getCategoriesResources: builder.query<CategoryResource[], void>({
            query: () => ENDPOINTS.categoriesResources(),
            providesTags: ['Categories'],
        }),

        // New unified filter settings endpoints
        getResourcesSettings: builder.query<{ hiddenCategories: string[] }, void>({
            query: () => ENDPOINTS.resourcesSettings(),
            providesTags: ['ResourceSettings'],
        }),
        saveResourcesSettings: builder.mutation<void, { hiddenCategories: string[] }>({
            query: (data) => ({
                url: ENDPOINTS.resourcesSettings(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ResourceSettings'],
        }),

        getPlanResources: builder.query<
            EventData[],
            { forecast: number; includeHistory?: boolean | string }
        >({
            query: ({ forecast, includeHistory }) => {
                if (includeHistory) {
                    let dateStr: string;
                    if (typeof includeHistory === 'string') {
                        dateStr = includeHistory;
                    } else {
                        const date = new Date();
                        date.setDate(date.getDate() - 42);
                        dateStr = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
                    }
                    return ENDPOINTS.planResources(forecast, { date: dateStr });
                }
                return ENDPOINTS.planResources(forecast);
            },
            providesTags: ['Plan'],
        }),
        getYearSummary: builder.query<
            {
                date: string;
                event?: string;
                eventId?: number | null;
                startTime?: string;
                endTime?: string;
                location?: string;
                excludedCategories?: number[];
                weekday?: number;
                canceled?: number;
            }[],
            number
        >({
            query: (year) => ENDPOINTS.yearSummary(year),
            providesTags: (result) => [
                { type: 'Plan', id: 'SUMMARY' },
                ...(result?.map((item) => ({ type: 'Plan' as const, id: item.date })) || []),
            ],
        }),
        getPersonalForecast: builder.query<
            {
                date: string;
                event?: string;
                eventId?: number | null;
                startTime?: string;
                endTime?: string;
                location?: string;
                excludedCategories?: number[];
                canceled?: number;
                assignments: {
                    categoryId: number;
                    categoryName: string;
                    categoryIcon: string;
                    resourceId: number;
                    resourceName: string;
                    isUserAssigned: boolean;
                    hasChangeRequest: boolean;
                    changeRequestedBy?: number;
                }[];
            }[],
            number | undefined
        >({
            query: (limit) => ENDPOINTS.personalForecast(limit),
            providesTags: (result) => [
                { type: 'Plan', id: 'PERSONAL_FORECAST' },
                ...(result?.map((item) => ({ type: 'Plan' as const, id: item.date })) || []),
            ],
        }),
        addPlanResource: builder.mutation<
            void,
            { date: string; resource: number; category: number }
        >({
            query: (data) => ({
                url: ENDPOINTS.planResources(0),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, arg) => [
                'Plan',
                { type: 'PlanResourcesAudit', id: `${arg.date}-${arg.category}` },
            ],
        }),
        deletePlanResource: builder.mutation<
            void,
            { date: string; category: number; resource: number }
        >({
            query: (data) => ({
                url: ENDPOINTS.planResources(0),
                method: 'DELETE',
                body: data,
            }),
            invalidatesTags: (_result, _error, arg) => [
                'Plan',
                { type: 'PlanResourcesAudit', id: `${arg.date}-${arg.category}` },
            ],
        }),

        addAbsent: builder.mutation<void, { date: string; resource: number }>({
            query: (data) => ({
                url: ENDPOINTS.planResourcesAbsent(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Plan'],
        }),
        removeAbsent: builder.mutation<void, { date: string; resource: number }>({
            query: (data) => ({
                url: ENDPOINTS.planResourcesAbsent(),
                method: 'DELETE',
                body: data,
            }),
            invalidatesTags: ['Plan'],
        }),

        addEvent: builder.mutation<void, { date: string; event: string }>({
            query: (data) => ({
                url: ENDPOINTS.plan(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Plan'],
        }),
        createChangeRequest: builder.mutation<
            void,
            { date: string; category: number; resource: number }
        >({
            query: (data) => ({
                url: ENDPOINTS.changeRequests(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Plan'],
        }),
        cancelChangeRequest: builder.mutation<
            void,
            { date: string; category: number; resource?: number }
        >({
            query: (data) => ({
                url: ENDPOINTS.changeRequests(),
                method: 'DELETE',
                body: data,
            }),
            invalidatesTags: ['Plan'],
        }),
        acceptChangeRequest: builder.mutation<
            void,
            {
                date: string;
                category: number;
                acceptingResourceId: number;
                requestingResource: number;
            }
        >({
            query: (data) => ({
                url: ENDPOINTS.changeRequests(),
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_result, _error, arg) => [
                'Plan',
                { type: 'PlanResourcesAudit', id: `${arg.date}-${arg.category}` },
            ],
        }),

        notifyChange: builder.mutation<
            void,
            {
                issuerId: number;
                issuerName: string;
                date: string;
                categoryId: number;
                categoryName: string;
            }
        >({
            query: (data) => ({
                url: ENDPOINTS.notifyChange(),
                method: 'POST',
                body: data,
            }),
        }),

        getMails: builder.query<Mail[], number>({
            query: (count) => ENDPOINTS.mail(count),
            providesTags: ['Mail'],
        }),

        getInstallStatus: builder.query<InstallationStatus, void>({
            query: () => ENDPOINTS.install(),
            providesTags: ['Settings'],
        }),
        postInstallStep: builder.mutation<InstallPostResponse, InstallPostRequest>({
            query: ({ step, ...data }) => ({
                url: ENDPOINTS.installStep(step),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Settings', 'Migrations'],
        }),

        getAgenda: builder.query<AgendaResponse[], { from: string; limit: number }>({
            query: ({ from, limit }) => ENDPOINTS.agenda(from, limit),
            providesTags: (result) => [
                { type: 'Plan', id: 'AGENDA' },
                // Also tag each date for fine-grained cache invalidation
                ...(result?.map((item) => ({ type: 'Plan' as const, id: item.date })) || []),
            ],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        addChurchToolsServices(
                            data.flatMap(
                                (agenda) =>
                                    agenda.services
                                        ?.filter((service) => service.name && service.responsible)
                                        .map((service) => service.name) ?? []
                            )
                        )
                    );
                } catch {}
            },
        }),

        getAdminData: builder.query<AdminData, void>({
            query: () => ENDPOINTS.admin(),
            providesTags: ['Admin'],
        }),
        getCategories: builder.query<Category[], void>({
            query: () => ENDPOINTS.categories(),
            providesTags: ['Categories'],
        }),
        addCategory: builder.mutation<
            void,
            {
                name: string;
                icon: string;
                group: string;
                active?: number;
                setupTime?: number;
                teardownTime?: number;
                sortPriority?: number;
                allowMultiple?: number;
                minAssignments?: number;
                maxAssignments?: number;
                notifyDaysBeforeWhenUnassigned?: number | null;
            }
        >({
            query: (data) => ({
                url: ENDPOINTS.categories(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Categories', 'Admin'],
        }),
        updateCategory: builder.mutation<
            void,
            {
                id: number;
                name: string;
                icon: string;
                group: string;
                active?: number;
                setupTime?: number;
                teardownTime?: number;
                sortPriority?: number;
                allowMultiple?: number;
                minAssignments?: number;
                maxAssignments?: number;
                notifyDaysBeforeWhenUnassigned?: number | null;
            }
        >({
            query: (data) => ({
                url: ENDPOINTS.categories(data.id),
                method: 'PUT',
                body: {
                    name: data.name,
                    icon: data.icon,
                    group: data.group,
                    active: data.active,
                    setupTime: data.setupTime,
                    teardownTime: data.teardownTime,
                    sortPriority: data.sortPriority,
                    allowMultiple: data.allowMultiple,
                    minAssignments: data.minAssignments,
                    maxAssignments: data.maxAssignments,
                    notifyDaysBeforeWhenUnassigned: data.notifyDaysBeforeWhenUnassigned ?? null,
                },
            }),
            invalidatesTags: ['Categories', 'Admin'],
        }),
        deleteCategory: builder.mutation<void, { id: number }>({
            query: (data) => ({
                url: ENDPOINTS.categories(data.id),
                method: 'DELETE',
            }),
            invalidatesTags: ['Categories', 'Admin'],
        }),
        addResource: builder.mutation<
            void,
            {
                name: string;
                mail: string;
                auth: string;
                active: boolean;
                admin: boolean;
                scheduleEditor: boolean;
                scheduleTemplateEditor: boolean;
                eventEditor: boolean;
                debug?: boolean;
            }
        >({
            query: (data) => ({
                url: ENDPOINTS.resources(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Admin'],
        }),
        updateResource: builder.mutation<
            void,
            {
                id: number;
                name: string;
                mail: string;
                auth: string;
                active: number;
                admin: number;
                scheduleEditor: number;
                scheduleTemplateEditor: number;
                eventEditor: number;
                debug?: number;
            }
        >({
            query: ({ id, ...data }) => ({
                url: ENDPOINTS.resources(id),
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Admin'],
        }),
        deleteResource: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: ENDPOINTS.resources(id),
                method: 'DELETE',
            }),
            invalidatesTags: ['Admin'],
        }),
        addResourceCategory: builder.mutation<
            void,
            { resource: number; category: number; assistant: boolean }
        >({
            query: (data) => ({
                url: ENDPOINTS.resourcesCategories(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Admin', 'Categories'],
        }),
        updateResourceCategory: builder.mutation<
            void,
            { resource: number; category: number; assistant: boolean }
        >({
            query: (data) => ({
                url: ENDPOINTS.resourcesCategories(),
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Admin', 'Categories'],
        }),
        deleteResourceCategory: builder.mutation<void, { resource: number; category: number }>({
            query: (data) => ({
                url: ENDPOINTS.resourcesCategories(),
                method: 'DELETE',
                body: data,
            }),
            invalidatesTags: ['Admin', 'Categories'],
        }),
        addCategoryResponsible: builder.mutation<void, { resource: number; category: number }>({
            query: (data) => ({
                url: ENDPOINTS.categoriesResponsible(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Admin'],
        }),
        deleteCategoryResponsible: builder.mutation<void, { resource: number; category: number }>({
            query: (data) => ({
                url: ENDPOINTS.categoriesResponsible(),
                method: 'DELETE',
                body: data,
            }),
            invalidatesTags: ['Admin'],
        }),
        addResourceProxy: builder.mutation<void, { resource: number; proxy: number }>({
            query: (data) => ({
                url: ENDPOINTS.resourcesProxies(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Admin'],
        }),
        deleteResourceProxy: builder.mutation<void, { resource: number; proxy: number }>({
            query: (data) => ({
                url: ENDPOINTS.resourcesProxies(),
                method: 'DELETE',
                body: data,
            }),
            invalidatesTags: ['Admin'],
        }),
        getResourcesProxies: builder.query<
            { proxyId: number; proxyName: string; userId: number; userName: string }[],
            number | void
        >({
            query: (resourceId) =>
                resourceId
                    ? `${ENDPOINTS.resourcesProxies()}/${resourceId}`
                    : ENDPOINTS.resourcesProxies(),
            providesTags: ['Admin'],
        }),

        // Events management endpoints
        getEvents: builder.query<Event[], void>({
            query: () => 'rest/Events',
            providesTags: ['Plan'],
        }),
        getEventDistinctValues: builder.query<{ names: string[]; locations: string[] }, void>({
            query: () => 'rest/Events/distinct',
            providesTags: ['Plan'],
        }),
        getEvent: builder.query<Event, number>({
            query: (id) => `rest/Events/${id}`,
            providesTags: (_result, _error, id) => [{ type: 'Plan', id: `EVENT_${id}` }],
        }),
        createEvent: builder.mutation<
            { id: number },
            {
                name: string;
                location: string;
                startTime: string;
                endTime: string;
                recurrenceType: 'weekly' | 'monthly' | 'yearly';
                recurrencePattern:
                    | number[]
                    | MonthlyRecurrencePattern[]
                    | YearlyRecurrencePattern[];
                active?: boolean;
                excludedCategories?: number[];
            }
        >({
            query: (data) => ({
                url: 'rest/Events',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Plan'],
        }),
        updateEvent: builder.mutation<
            void,
            {
                id: number;
                name: string;
                location: string;
                startTime: string;
                endTime: string;
                recurrenceType: 'weekly' | 'monthly' | 'yearly';
                recurrencePattern:
                    | number[]
                    | MonthlyRecurrencePattern[]
                    | YearlyRecurrencePattern[];
                active?: boolean;
                excludedCategories?: number[];
            }
        >({
            query: ({ id, ...data }) => ({
                url: `rest/Events/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Plan', id: `EVENT_${id}` },
                'Plan',
            ],
        }),
        deleteEvent: builder.mutation<void, number>({
            query: (id) => ({
                url: `rest/Events/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Plan'],
        }),

        createPlanEvent: builder.mutation<void, EventData>({
            query: (data) => ({
                url: ENDPOINTS.plan(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Plan'],
        }),
        updatePlanEvent: builder.mutation<void, EventData>({
            query: (data) => ({
                url: ENDPOINTS.planEvent(data.date),
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Plan'],
        }),
        deletePlanEvent: builder.mutation<void, { date: string }>({
            query: (data) => ({
                url: ENDPOINTS.planEvent(data.date),
                method: 'DELETE',
            }),
            invalidatesTags: ['Plan'],
        }),

        // Schedule endpoints
        getSchedule: builder.query<ScheduleItem[], { date: string } | void>({
            query: (params) => {
                if (params?.date) {
                    return `rest/Schedule/${params.date}`;
                }
                return 'rest/Schedule';
            },
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({
                              type: 'Schedule' as const,
                              id,
                          })),
                          { type: 'Schedule', id: 'LIST' },
                      ]
                    : [{ type: 'Schedule', id: 'LIST' }],
        }),
        getRecentSchedules: builder.query<RecentSchedule[], void>({
            query: () => 'rest/Schedule/recent',
            providesTags: [{ type: 'Schedule', id: 'RECENT' }],
        }),
        copySchedule: builder.mutation<{ copied: number }, { fromDate: string; toDate: string }>({
            query: (data) => ({
                url: 'rest/Schedule/copy',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Schedule', { type: 'Schedule', id: 'LIST' }],
        }),
        createScheduleItem: builder.mutation<{ id: ScheduleItem['id'] }, Omit<ScheduleItem, 'id'>>({
            query: (data) => ({
                url: 'rest/Schedule',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result) => [
                'Schedule',
                { type: 'Schedule', id: result?.id ?? 'undefined' },
                { type: 'Schedule', id: 'LIST' },
            ],
        }),
        updateScheduleItem: builder.mutation<
            { id: ScheduleItem['id'] },
            Omit<ScheduleItem, 'eventDate'>
        >({
            query: ({ id, ...data }) => ({
                url: `rest/Schedule/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                'Schedule',
                { type: 'Schedule', id },
                { type: 'Schedule', id: 'LIST' },
            ],
        }),
        deleteScheduleItem: builder.mutation<{ id: ScheduleItem['id'] }, ScheduleItem['id']>({
            query: (id) => ({
                url: `rest/Schedule/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id) => [
                'Schedule',
                { type: 'Schedule', id },
                { type: 'Schedule', id: 'LIST' },
            ],
        }),
        clearSchedule: builder.mutation<{ deleted: number }, { date: string }>({
            query: ({ date }) => ({
                url: `rest/Schedule/all/${date}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Schedule', { type: 'Schedule', id: 'LIST' }],
        }),

        // Schedule Templates endpoints
        getScheduleTemplates: builder.query<ScheduleTemplate[], void>({
            query: () => 'rest/ScheduleTemplates',
            providesTags: ['ScheduleTemplates'],
        }),
        createScheduleTemplate: builder.mutation<
            { id: number },
            Pick<ScheduleTemplate, 'template' | 'duration' | 'responsible' | 'tags'>
        >({
            query: (data) => ({
                url: 'rest/ScheduleTemplates',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ScheduleTemplates'],
        }),
        updateScheduleTemplate: builder.mutation<void, ScheduleTemplate>({
            query: ({ id, ...data }) => ({
                url: `rest/ScheduleTemplates/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['ScheduleTemplates'],
        }),
        deleteScheduleTemplate: builder.mutation<void, ScheduleTemplate['id']>({
            query: (id) => ({
                url: `rest/ScheduleTemplates/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ScheduleTemplates'],
        }),

        // Schedule Template Sets endpoints
        getScheduleTemplateSets: builder.query<ScheduleTemplateSet[], void>({
            query: () => 'rest/ScheduleTemplateSets',
            providesTags: ['ScheduleTemplateSets'],
        }),
        createScheduleTemplateSet: builder.mutation<
            { id: number },
            { name: string; templateIds: number[] }
        >({
            query: (data) => ({
                url: 'rest/ScheduleTemplateSets',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ScheduleTemplateSets'],
        }),
        updateScheduleTemplateSet: builder.mutation<
            void,
            { id: number; name: string; templateIds: number[] }
        >({
            query: ({ id, ...data }) => ({
                url: `rest/ScheduleTemplateSets/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['ScheduleTemplateSets'],
        }),
        deleteScheduleTemplateSet: builder.mutation<void, number>({
            query: (id) => ({
                url: `rest/ScheduleTemplateSets/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ScheduleTemplateSets'],
        }),

        applyScheduleTemplateSet: builder.mutation<
            { created: number },
            { templateSetId: number; toDate: string }
        >({
            query: (data) => ({
                url: 'rest/Schedule/applyTemplateSet',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Schedule', { type: 'Schedule', id: 'LIST' }],
        }),

        // Migration endpoints
        getMigrationStatus: builder.query<MigrationResponse, void>({
            query: () => 'rest/Migrations',
            providesTags: ['Migrations'],
        }),
        applyMigration: builder.mutation<MigrationResponse, { migration: string }>({
            query: (data) => ({
                url: 'rest/Migrations',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Migrations'],
        }),
        applyAllMigrations: builder.mutation<MigrationResponse, void>({
            query: () => ({
                url: 'rest/Migrations',
                method: 'POST',
                body: { all: true },
            }),
            invalidatesTags: ['Migrations'],
        }),

        // Category Assignment Requests endpoints (User)
        getCategoryRequests: builder.query<
            { success: boolean; data: CategoryRequest[] },
            { status: CategoryRequest['status'] | 'all' }
        >({
            query: ({ status }) => ENDPOINTS.categoryRequestsByStatus(status),
            providesTags: ['CategoryRequests'],
        }),
        createCategoryRequest: builder.mutation<
            { success: boolean; id: number; message: string },
            { categoryId: number; requestType: 'join' | 'leave'; message?: string }
        >({
            query: (data) => ({
                url: ENDPOINTS.categoryRequests(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['CategoryRequests', 'Admin'],
        }),
        deleteCategoryRequest: builder.mutation<{ success: boolean; message: string }, number>({
            query: (id) => ({
                url: ENDPOINTS.categoryRequests(id),
                method: 'DELETE',
            }),
            invalidatesTags: ['CategoryRequests', 'Admin'],
        }),

        // Category Assignment Requests endpoints (Category Responsible)
        getResponsibleCategoryRequests: builder.query<
            { success: boolean; data: CategoryRequest[] },
            { status: CategoryRequest['status'] | 'all' }
        >({
            query: ({ status }) => ENDPOINTS.manageCategoryRequestsByStatus(status),
            providesTags: ['CategoryRequests'],
        }),

        // Category Assignment Requests endpoints (Admin)
        getAdminCategoryRequests: builder.query<
            { success: boolean; data: CategoryRequest[] },
            { status: CategoryRequest['status'] | 'all' }
        >({
            query: ({ status }) => ENDPOINTS.manageCategoryRequestsByStatus(status),
            providesTags: ['CategoryRequests'],
        }),
        updateAdminCategoryRequest: builder.mutation<
            { success: boolean; message: string },
            { id: number; action: 'accept' | 'deny'; adminNote?: string }
        >({
            query: ({ id, ...data }) => ({
                url: ENDPOINTS.manageCategoryRequests(id),
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['CategoryRequests', 'Admin', 'Categories', 'UserInfo'],
        }),
        getLog: builder.query<
            { lines: string[]; total: number },
            { offset: number; limit: number; levels?: string }
        >({
            query: (params) => ({
                url: ENDPOINTS.log(params),
            }),
        }),
        scheduleSearch: builder.query<
            {
                values: Array<{
                    title: string;
                    duration?: number | string | null;
                    responsible?: string | null;
                    note?: string | null;
                    tags?: string | null;
                }>;
            },
            { tags: string; title: string; limit?: number }
        >({
            query: ({ tags, title, limit }) => {
                const params = new URLSearchParams();
                params.set('tags', tags);
                if (title) params.set('title', title);
                if (limit) params.set('limit', String(limit));
                return `rest/Schedule/search?${params.toString()}`;
            },
            providesTags: ['Schedule'],
        }),
        getMetrics: builder.query<
            MetricsData,
            { from?: string; to?: string; resourceId?: number } | void
        >({
            query: (params) => ENDPOINTS.metrics(params ?? undefined),
            providesTags: ['Metrics'],
        }),
        recordMetricsEvent: builder.mutation<{ recorded: boolean }, MetricsEventPayload>({
            query: (data) => ({
                url: ENDPOINTS.metricsEvents(),
                method: 'POST',
                body: data,
            }),
        }),
        getEventAudit: builder.query<EventAuditEntry[], { date: string; limit?: number }>({
            query: ({ date, limit }) => ENDPOINTS.eventAudit(date, limit),
            providesTags: (_result, _error, arg) => [{ type: 'PlanResourcesAudit', id: arg.date }],
        }),

        // Debug screenshots
        getScreenshots: builder.query<ScreenshotMeta[], void>({
            query: () => ENDPOINTS.screenshots(),
            providesTags: ['Screenshots'],
        }),
        uploadScreenshot: builder.mutation<
            { id: number },
            {
                image: string;
                url?: string;
                width?: number;
                height?: number;
                userAgent?: string;
            }
        >({
            query: (data) => ({
                url: ENDPOINTS.screenshots(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Screenshots', 'Metrics'],
        }),
        deleteScreenshot: builder.mutation<void, number>({
            query: (id) => ({
                url: ENDPOINTS.screenshots(id),
                method: 'DELETE',
            }),
            invalidatesTags: ['Screenshots'],
        }),
    }),
});

export const {
    useAuthenticateMutation,
    useLogoutMutation,
    useGetSessionTimestampQuery,
    useReloadSessionMutation,
    useGetUsersQuery,
    useGetUserInfoQuery,
    useUpdateUserInfoMutation,
    useGetSettingsQuery,
    useGetInstallStatusQuery,
    useGetLastSeenQuery,
    usePostInstallStepMutation,
    useGetCategoriesResourcesQuery,
    useGetResourcesSettingsQuery,
    useSaveResourcesSettingsMutation,
    useGetPlanResourcesQuery,
    useGetYearSummaryQuery,
    useGetPersonalForecastQuery,
    useAddPlanResourceMutation,
    useDeletePlanResourceMutation,
    useAddAbsentMutation,
    useRemoveAbsentMutation,
    useAddEventMutation,
    useCreateChangeRequestMutation,
    useCancelChangeRequestMutation,
    useAcceptChangeRequestMutation,
    useNotifyChangeMutation,
    useGetMailsQuery,
    useGetAgendaQuery,
    useGetAdminDataQuery,
    useGetCategoriesQuery,
    useAddCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
    useAddResourceMutation,
    useUpdateResourceMutation,
    useDeleteResourceMutation,
    useAddResourceCategoryMutation,
    useUpdateResourceCategoryMutation,
    useDeleteResourceCategoryMutation,
    useAddCategoryResponsibleMutation,
    useDeleteCategoryResponsibleMutation,
    useAddResourceProxyMutation,
    useDeleteResourceProxyMutation,
    useGetResourcesProxiesQuery,
    useGetEventsQuery,
    useGetEventDistinctValuesQuery,
    useGetEventQuery,
    useCreateEventMutation,
    useUpdateEventMutation,
    useDeleteEventMutation,
    useCreatePlanEventMutation,
    useUpdatePlanEventMutation,
    useDeletePlanEventMutation,
    useGetScheduleQuery,
    useGetRecentSchedulesQuery,
    useCopyScheduleMutation,
    useCreateScheduleItemMutation,
    useUpdateScheduleItemMutation,
    useDeleteScheduleItemMutation,
    useClearScheduleMutation,
    useGetScheduleTemplatesQuery,
    useCreateScheduleTemplateMutation,
    useUpdateScheduleTemplateMutation,
    useDeleteScheduleTemplateMutation,
    useGetScheduleTemplateSetsQuery,
    useCreateScheduleTemplateSetMutation,
    useUpdateScheduleTemplateSetMutation,
    useDeleteScheduleTemplateSetMutation,
    useApplyScheduleTemplateSetMutation,
    useGetMigrationStatusQuery,
    useApplyMigrationMutation,
    useApplyAllMigrationsMutation,
    useGetCategoryRequestsQuery,
    useGetResponsibleCategoryRequestsQuery,
    useCreateCategoryRequestMutation,
    useDeleteCategoryRequestMutation,
    useGetAdminCategoryRequestsQuery,
    useUpdateAdminCategoryRequestMutation,
    useGetLogQuery,
    useScheduleSearchQuery,
    useGetMetricsQuery,
    useRecordMetricsEventMutation,
    useGetEventAuditQuery,
    useGetScreenshotsQuery,
    useUploadScreenshotMutation,
    useDeleteScreenshotMutation,
} = apiSlice;
